import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";

import Spinner from "../../UI/Spinner/Spinner";
import ErrorPanel, { ErrorSize } from "../../UI/ErrorPanel/ErrorPanel";

import * as PaymentActions from "../../../middleware/Store/Actions/PaymentsActions";
import * as InvoicesActions from "../../../middleware/Store/Actions/InvoicesActions";
import LogService from "../../../middleware/CommonSdk/LogService";

import { faMoneyCheck } from "@fortawesome/free-solid-svg-icons";
import { faCreditCard } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const LOG_TAG = "BILLING_UPDATE";

const BillingUpdate = (props) => {
    const intl = useIntl();
    const [progress, setProgress] = useState(false);
    const [error, setError] = useState(null);
    const [selectedOption, setSelectedOption] = useState("creditCard");
    const accountId = useRef(null);

    useEffect(() => {
        LogService.trace(LOG_TAG, "UseEffect LoadZuoraIFrame");
        setError(null);
        if (props.user) {
            LoadZuoraIFrame();
        }
    }, [props.user, selectedOption])

    let LoadZuoraIFrame = () => {
        setProgress(true);
        let callback = response => {
            LogService.info(LOG_TAG, "LoadZuoraIFrame", "callback IFrame received", accountId.current, response.refId);
            if (response.success) {
                LogService.info(LOG_TAG, "LoadZuoraIFrame", "callback IFrame received", "SUCCESS");
                setProgress(true);

                let needCreateInvoiceAccount = Promise.resolve();
                if (accountId.current == null) {
                    LogService.info(LOG_TAG, "LoadZuoraIFrame", "create invoice account");
                    needCreateInvoiceAccount = props.createInvoiceAccount(props.user, response.refId);
                }

                needCreateInvoiceAccount
                    .then(() => props.setAsDefault(props.user.id, response.refId))
                    .then(() => {
                        setProgress(false);
                        setError(null);

                        if (props.onActionSuccess)
                            props.onActionSuccess();
                    })
                    .catch(errorPayload => {
                        LogService.error(LOG_TAG, "LoadZuoraIFrame", "callback create invoices account or set as default", "FAILED:", errorPayload.errorCode, errorPayload.errorMessage);
                        setProgress(false);
                        setError(errorPayload);

                        if (props.onActionFailed)
                            props.onActionFailed(errorPayload);
                    });
            } else {
                LogService.error(LOG_TAG, "LoadZuoraIFrame", "callback IFrame received", "FAILED:" + response.errorCode);
                setProgress(false);
                const errorPayload = {
                    errorCode: response.errorCode,
                    errorMessage: response.errorMessage
                };
                setError(errorPayload);
                if (props.onActionFailed)
                    props.onActionFailed(errorPayload);
            }
        }

        let errorMessageCallback = function (key, code, message) {
            LogService.error(LOG_TAG, "LoadZuoraIFrame - callback IFrame KO:", key + " : " + code + " : " + message);
            let errorMessage = "";

            switch (key) {
                // Overwrite error messages generated by client-side validation.
                case "creditCardNumber":
                    if (code === "001") {
                        errorMessage = intl.messages.errorMessageCreditCardNumber001;
                    } else {
                        errorMessage = intl.messages.errorMessageCreditCardNumberOther;
                    }
                    break;
                case "cardSecurityCode":
                    errorMessage = intl.messages.errorMessageCardSecurityCode;
                    break;
                case "creditCardExpirationYear":
                    errorMessage = intl.messages.errorMessageCreditCardExpirationYear;
                    break;
                case "creditCardExpirationMonth":
                    errorMessage = intl.messages.errorMessageCreditCardExpirationMonth;
                    break;
                case "creditCardType":
                    errorMessage = intl.messages.errorMessageCreditCardType;
                    break;
                case "creditCardHolderName":
                    errorMessage = intl.messages.errorMessageCreditCardHolderName;
                    break;
                case "error":
                    errorMessage = intl.messages.errorMessageCreditCard;
                    break;
                default:
                    errorMessage = intl.messages.errorMessageCreditCardValidation;
                    break;
            }

            setProgress(false);
            window.Z.sendErrorMessageToHpm(key, errorMessage);
            return;
        }

        let signatureCallback = ccsignature => {
            LogService.trace(LOG_TAG, "LoadZuoraIFrame - signatureCallback:", "ok");
            accountId.current = ccsignature?.field_accountId;

            let params = {
                signature: ccsignature.signature,
                tenantId: ccsignature.tenantId,
                key: ccsignature.key,
                token: ccsignature.token,
                id: ccsignature.id,
                url: ccsignature.url,
                style: "inline",
                submitEnabled: "true",
                locale: intl.locale
            }

            if (ccsignature.field_accountId) {
                params.field_accountId = ccsignature.field_accountId;
            }

            setProgress(false);
            window.Z.renderWithErrorHandler(params, {}, callback, errorMessageCallback);
        }

        let signatureErrorCallback = errorPayload => {
            LogService.error(LOG_TAG, "LoadZuoraIFrame - signatureErrorCallback:", "ko");
            setProgress(false);
            setError(errorPayload);
        }

        props.getRSASignatureForMethod(props.user.id, selectedOption)
            .then(ccsignature => {
                signatureCallback(ccsignature);
            })
            .catch(errorPayload => {
                signatureErrorCallback(errorPayload);
            });
    }

    function errorDisplay() {
        if (error) {
            return (
                <div>
                    <ErrorPanel id="option-error" size={ErrorSize.Small}>{error.errorCode} : {error.errorMessage}</ErrorPanel>
                </div>
            )
        }
    }

    function handlePaymentMethodChange(selectedMethod) {
        setSelectedOption(selectedMethod);
        setError(null);
    }

    return (
        <div id="accordion">
            <div className="panel bg-fog mt-3 mb-3">{intl.formatMessage({ id: "billingWarning" }, { b: chunks => <b>{chunks}</b> })}</div>

            <div className="card">
                <div className="card-header justify-content-between mb-3 h5" id="headingOne">
                    <a className="btn btn-link" data-toggle="collapse" href="#collapseOne" aria-expanded="true" data-target="#collapseOne" aria-controls="collapseOne"
                        onClick={e => handlePaymentMethodChange("creditCard")}
                    >
                        {intl.messages.billingCreditCard}
                    </a>
                    <FontAwesomeIcon icon={faCreditCard} />
                </div>

                <div id="collapseOne" className="collapse show" aria-labelledby="headingOne" data-parent="#accordion">

                    <div className="card-body" style={{ minHeight: "100px" }}>
                        {progress && selectedOption === "creditCard" && <Spinner />}
                        {!progress && selectedOption === "creditCard" && <div id="zuora_payment" />}
                        {errorDisplay()}
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-header justify-content-between mb-3 h5" id="headingTwo">
                    <a className="btn btn-link" data-toggle="collapse" href="#collapseTwo" aria-expanded="false" data-target="#collapseTwo" aria-controls="collapseTwo"
                        onClick={e => handlePaymentMethodChange("sepa")}
                    >
                        {intl.messages.billingSEPA}
                    </a>
                    <span><FontAwesomeIcon icon={faMoneyCheck} /></span>
                </div>
                <div id="collapseTwo" className="collapse" aria-labelledby="headingTwo" data-parent="#accordion">
                    <div className="panel bg-fog mb-3">{intl.messages.informationSEPA}</div>
                    <div className="card-body" style={{ minHeight: "100px" }}>
                        {progress && selectedOption === "sepa" && <Spinner />}
                        {!progress && selectedOption === "sepa" && <div id="zuora_payment" />}
                        {errorDisplay()}
                    </div>
                </div>
            </div>
        </div>
    );
}

BillingUpdate.propTypes = {
    onActionSuccess: PropTypes.func,
    onActionFailed: PropTypes.func
};

BillingUpdate.defaultProps = {
    onActionSuccess: null,
    onActionFailed: null
}

const mapStateToProps = state => {
    return {
        user: state.contacts.user
    };
}

const mapDispatchToProps = dispatch => {
    return {
        getRSASignatureForMethod: (userId, paymentMethod) => dispatch(PaymentActions.getRSASignatureForMethod(userId, paymentMethod)),
        setAsDefault: (userId, paymentMethodId) => dispatch(PaymentActions.setAsDefault(userId, paymentMethodId)),
        createInvoiceAccount: (user, paymentMethodId) => dispatch(InvoicesActions.createInvoiceAccount(user, paymentMethodId))
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(BillingUpdate)